import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VRG-761 — `deleteUnusedTags` is the operator's explicit tick in the confirmation modal.
 *
 * Absent or false means: delete the box, DETACH every tag inside it, destroy nothing. True means
 * additionally delete the tags the server counted as unused — never a used one, which the server
 * enforces with the shared predicate and, independently, with two foreign keys.
 */
type RemoveParams = { id: string; deleteUnusedTags?: boolean }

export const usePackageDelete = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: ['packages', 'delete'],
    mutationFn: async ({ id, deleteUnusedTags = false }: RemoveParams) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/packages/${id}`,
        params: deleteUnusedTags ? { deleteUnusedTags: 'true' } : undefined,
      })
      // `createApiCall` RESOLVES on 4xx/5xx rather than rejecting (see
      // `usePackageConsignmentDestination` for the same note), so a 403 from the new
      // `internal_package_delete` gate would otherwise read as a successful delete and the row
      // would vanish from the list until the next refetch brought it back.
      const status = response?.status
      if (typeof status === 'number' && (status < 200 || status >= 300)) {
        const error: Error & { status?: number } = new Error(
          (response?.data as any)?.message || 'Could not delete this package',
        )
        error.status = status
        throw error
      }
      return response.data as {
        success: boolean
        tagsDetached: number
        tagsDeleted: number
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] })
      // Detached tags change the internal Tags list too — they are still there, just no longer
      // in a box — and deleted ones leave it. Either way the list the operator may be looking at
      // beside this one is now stale.
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })

  return mutation
}

